import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import {
  SEND_EMPLOYEE_REQUEST_FAILURE,
  SEND_EMPLOYEE_REQUEST_SUCCESS,
  SendEmployeeRequestAction,
} from "../employees.actions";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EmployeesService } from "../employees.service";
import { merge, Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  map,
  take,
  takeUntil,
} from "rxjs/operators";
import { Employee, EmployeeRequest } from "../employees.interface";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";

type DialogEmployee = Employee;

const DEFAULT_BODY =
  '<p>Dear team,</p><p>We have implemented a centrally managed email signature. By logging in with your Google account, you can access your personal profile settings at <a href="https://app.gsignature.com/">Gmail Signature Manager</a>. Please get familiar with your data and correct/update it as necessary.</p>';

@Component({
  selector: "app-employee-request-dialog",
  templateUrl: "./employee-request-dialog.component.html",
  styleUrls: ["./employee-request-dialog.component.scss"],
  standalone: false,
})
export class EmployeeRequestDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  sendingRequest = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  allEmployees: DialogEmployee[] = [];
  filteredEmployees: DialogEmployee[] = [];
  selectedEmployees: DialogEmployee[] = [];
  searchControl = new FormControl("");
  isLoadingEmployees = false;
  editorHeight = 540;
  displayedColumns: string[] = ["select", "fullName", "email"];
  private unsubscribe$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<EmployeeRequestDialogComponent>,
    private store: Store,
    private actions$: Actions,
    private employeesService: EmployeesService,
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      subject: new FormControl("The new settings for email signatures", [
        (control) => Validators.required(control),
      ]),
      body: new FormControl(DEFAULT_BODY, [
        (control) => Validators.required(control),
      ]),
    });
    this.loadEmployees();
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getEmployeePhone(employee: DialogEmployee): string {
    const dynamicVariables = employee.dynamicVariables ?? {};
    const mobile = dynamicVariables["mobileNumber"];
    const phone = dynamicVariables["phoneNumber"];
    if (typeof mobile === "string" && mobile.trim()) {
      return mobile.trim();
    }
    if (typeof phone === "string" && phone.trim()) {
      return phone.trim();
    }
    return employee.mobileNumber || employee.phoneNumber || "";
  }

  private loadEmployees(): void {
    this.isLoadingEmployees = true;
    this.employeesService.getNotExcludedEmployeesWithAliases().subscribe({
      next: (data) => {
        this.allEmployees = data.map((employee) => ({
          ...employee,
          employeeId: employee.employeeId,
        }));
        this.filterEmployees();
        this.isLoadingEmployees = false;
      },
      error: () => {
        this.isLoadingEmployees = false;
      },
    });
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => this.filterEmployees());
  }

  private filterEmployees(): void {
    const searchValue = this.searchControl.value?.toLowerCase() || "";

    if (!searchValue) {
      this.filteredEmployees = [...this.allEmployees];
      return;
    }

    this.filteredEmployees = this.allEmployees.filter((emp) => {
      return (
        emp.fullName?.toLowerCase().includes(searchValue) ||
        emp.email?.toLowerCase().includes(searchValue) ||
        this.getEmployeePhone(emp).toLowerCase().includes(searchValue)
      );
    });
  }

  toggleEmployeeSelection(employee: DialogEmployee): void {
    const index = this.selectedEmployees.findIndex(
      (e) => e.employeeId === employee.employeeId,
    );
    if (index === -1) {
      this.selectedEmployees.push(employee);
    } else {
      this.selectedEmployees.splice(index, 1);
    }
  }

  isEmployeeSelected(employee: DialogEmployee): boolean {
    return this.selectedEmployees.some(
      (e) => e.employeeId === employee.employeeId,
    );
  }

  isAllSelected(): boolean {
    return (
      this.filteredEmployees.length > 0 &&
      this.filteredEmployees.every((emp) => this.isEmployeeSelected(emp))
    );
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.filteredEmployees.forEach((emp) => {
        const index = this.selectedEmployees.findIndex(
          (e) => e.employeeId === emp.employeeId,
        );
        if (index !== -1) {
          this.selectedEmployees.splice(index, 1);
        }
      });
    } else {
      this.filteredEmployees.forEach((emp) => {
        if (!this.isEmployeeSelected(emp)) {
          this.selectedEmployees.push(emp);
        }
      });
    }
  }

  submit() {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid || this.selectedEmployees.length === 0) {
      return;
    }

    this.sendingRequest = true;
    const emails = this.selectedEmployees
      .map((emp) => emp.email)
      .filter((email): email is string => !!email);
    const { subject, body } = this.form.value as {
      subject: string;
      body: string;
    };
    const payload: EmployeeRequest = {
      subject: subject ?? "",
      body,
      emails,
      channel: "email",
    };

    merge(
      this.actions$.pipe(
        ofType(SEND_EMPLOYEE_REQUEST_SUCCESS),
        map(() => true),
      ),
      this.actions$.pipe(
        ofType(SEND_EMPLOYEE_REQUEST_FAILURE),
        map(() => false),
      ),
    )
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe((success) => {
        this.sendingRequest = false;
        if (success) {
          this.close(true);
        }
      });

    this.store.dispatch(new SendEmployeeRequestAction(payload));
  }

  close(success?: boolean) {
    this.dialogRef.close(success);
  }
}
